import { RefreshCcw, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/auth/useAuth";
import { AppGrid } from "@/components/AppGrid";
import { CategoryFilter } from "@/components/CategoryFilter";
import {
  CollectionTabs,
  type CollectionId,
} from "@/components/CollectionTabs";
import { DiscoveryDebugPanel } from "@/components/DiscoveryDebugPanel";
import { EmptyState } from "@/components/EmptyState";
import { AppGridSkeleton } from "@/components/LoadingSkeleton";
import { ViewToggle, type ViewMode } from "@/components/ViewToggle";
import { useAppCatalog } from "@/hooks/useAppCatalog";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useFavorites } from "@/hooks/useFavorites";
import { useRecents } from "@/hooks/useRecents";
import { AppShell } from "@/layout/AppShell";
import { useShell } from "@/layout/shellContext";
import { resolveEntitlements } from "@/services/entitlementEngine";
import type { AppEntitlement } from "@/types/app";
import { isTroubleshootMode } from "@/utils/troubleshoot";

export function MyAppsPage() {
  return (
    <AppShell>
      <MyAppsContent />
    </AppShell>
  );
}

function MyAppsContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { account, claims } = useAuth();
  const catalog = useAppCatalog();
  const { favorites, toggle } = useFavorites();
  const { recents, push } = useRecents();
  const { query } = useShell();
  const q = useDebouncedValue(query.trim().toLowerCase(), 120);
  const troubleshoot = isTroubleshootMode(location.search);

  const [collection, setCollection] = useState<CollectionId>("all");
  const [category, setCategory] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>("grid");

  const resolution = useMemo(
    () => resolveEntitlements(catalog.apps, claims),
    [catalog.apps, claims],
  );

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const e of resolution.allowed) set.add(e.app.category);
    return Array.from(set).sort();
  }, [resolution.allowed]);

  const favoriteSet = useMemo(() => new Set(favorites), [favorites]);

  const recentList = useMemo(() => {
    const byId = resolution.byId;
    return recents
      .map((id) => byId[id])
      .filter((x): x is AppEntitlement => !!x && x.visible);
  }, [recents, resolution.byId]);

  const counts: Record<CollectionId, number> = {
    all: resolution.allowed.length,
    favorites: resolution.allowed.filter((e) => favoriteSet.has(e.appId)).length,
    recents: recentList.length,
  };

  let list: AppEntitlement[] = resolution.allowed;
  if (collection === "favorites") list = list.filter((e) => favoriteSet.has(e.appId));
  if (collection === "recents") list = recentList;
  if (category) list = list.filter((e) => e.app.category === category);
  if (q) {
    list = list.filter((e) => {
      const hay = `${e.app.name} ${e.app.description} ${e.app.category}`.toLowerCase();
      return hay.includes(q);
    });
  }

  const handleLaunch = (e: AppEntitlement) => {
    push(e.appId);
    if (!e.app.url) return;
    if (e.app.url.startsWith("/")) {
      navigate(e.app.url);
      return;
    }
    if (e.app.launchType === "same_tab") {
      window.location.assign(e.app.url);
    } else {
      window.open(e.app.url, "_blank", "noopener");
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <HeroBanner />

      <CollectionTabs
        value={collection}
        onChange={setCollection}
        counts={counts}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <CategoryFilter
          categories={categories}
          selected={category}
          onSelect={setCategory}
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={catalog.refresh}
            className="btn-ghost text-xs"
            disabled={catalog.loading}
          >
            <RefreshCcw className="h-3.5 w-3.5" />
            {catalog.loading ? "Refreshing…" : "Refresh"}
          </button>
          <ViewToggle value={view} onChange={setView} />
        </div>
      </div>

      {catalog.loading ? (
        <AppGridSkeleton />
      ) : list.length === 0 ? (
        <EmptyState
          icon={<Sparkles className="h-6 w-6" />}
          title={
            q || category
              ? "No matching apps"
              : collection === "favorites"
                ? "No favorites yet"
                : collection === "recents"
                  ? "No recent activity"
                  : "No applications assigned"
          }
          description={
            q || category
              ? "Try a different search term or clear the category filter."
              : collection === "favorites"
                ? "Click the star on any tile to pin it here."
                : collection === "recents"
                  ? "Apps you launch will show up here so you can find them fast."
                  : catalog.diagnostics.summary
          }
        />
      ) : (
        <AppGrid
          entitlements={list}
          favorites={favoriteSet}
          view={view}
          onToggleFavorite={toggle}
          onLaunch={handleLaunch}
        />
      )}

      {catalog.error ? (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-800">
          {catalog.error}
        </div>
      ) : null}

      {catalog.warnings.length ? (
        <details className="rounded border border-slate-200 bg-white p-3 text-xs text-slate-600">
          <summary className="cursor-pointer font-semibold">
            Admin diagnostics ({catalog.source})
          </summary>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {catalog.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </details>
      ) : null}

      {troubleshoot ? (
        <DiscoveryDebugPanel
          diagnostics={catalog.diagnostics}
          resolution={resolution}
          account={account}
          claims={claims}
        />
      ) : null}
    </div>
  );
}

function HeroBanner() {
  const { account, claims } = useAuth();
  const displayName =
    account?.name ||
    (claims?.name as string | undefined) ||
    [claims?.given_name, claims?.family_name].filter(Boolean).join(" ").trim() ||
    account?.username?.split("@")[0] ||
    "there";
  const upn =
    account?.username ||
    (claims?.preferred_username as string | undefined) ||
    "";
  return (
    <div className="rounded-tile bg-gradient-to-r from-brand-500 via-brand-600 to-brand-700 p-6 text-white shadow-tile">
      <h1 className="text-xl font-semibold">Welcome back, {displayName}.</h1>
      {upn ? (
        <p className="mt-0.5 text-xs text-white/80">{upn}</p>
      ) : null}
      <p className="mt-2 text-sm text-white/90">
        Your applications, ready when you are.
      </p>
    </div>
  );
}
